import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';

/**
 * Client HTTP partagé pour le frontend (US-AUTH-02).
 *
 * - `baseURL: /api` est proxifié par Vite vers http://localhost:3001
 *   (configuré dans `vite.config.ts`). En prod, Caddy fait pareil.
 * - `withCredentials: true` permet d'envoyer le cookie httpOnly du refresh
 *   token (`mygmao_refresh`) automatiquement vers `/auth/*`.
 * - L'access token est conservé EN MÉMOIRE seulement (pas localStorage —
 *   protection XSS). Le refresh cookie le restaure au reload.
 * - Intercepteur de réponse : si 401, tente un refresh automatique puis
 *   rejoue la requête (une seule fois pour éviter la boucle).
 */
export const api = axios.create({
  baseURL: '/api',
  withCredentials: true,
  timeout: 15000,
});

let accessTokenInMemory: string | null = null;

/** Définit le token d'accès courant (appelé par AuthContext après login/refresh). */
export function setAccessToken(token: string | null): void {
  accessTokenInMemory = token;
}

export function getAccessToken(): string | null {
  return accessTokenInMemory;
}

// ─── Intercepteurs ──────────────────────────────────────────────────────────

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (accessTokenInMemory && !config.headers.has('Authorization')) {
    config.headers.set('Authorization', `Bearer ${accessTokenInMemory}`);
  }
  return config;
});

/**
 * Refresh-on-401 — une tentative max par requête (`_retried` flag).
 * Si le refresh échoue, l'utilisateur est déconnecté côté UI via
 * l'event `auth:logout`.
 */
type RetriableConfig = InternalAxiosRequestConfig & { _retried?: boolean };

let pendingRefresh: Promise<string> | null = null;

async function performRefresh(): Promise<string> {
  if (!pendingRefresh) {
    pendingRefresh = axios
      .post<{ data: { access_token: string } }>('/api/auth/refresh', {}, { withCredentials: true })
      .then((res) => {
        const token = res.data.data.access_token;
        setAccessToken(token);
        return token;
      })
      .finally(() => {
        pendingRefresh = null;
      });
  }
  return pendingRefresh;
}

api.interceptors.response.use(
  (response) => response,
  async (err: AxiosError) => {
    const cfg = err.config as RetriableConfig | undefined;
    const status = err.response?.status;
    if (
      status === 401 &&
      cfg &&
      !cfg._retried &&
      // Ne pas tenter de refresh si la requête échouée EST le refresh ou le login
      !cfg.url?.includes('/auth/refresh') &&
      !cfg.url?.includes('/auth/login')
    ) {
      cfg._retried = true;
      try {
        const token = await performRefresh();
        cfg.headers = cfg.headers ?? {};
        (cfg.headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
        return api.request(cfg);
      } catch {
        setAccessToken(null);
        window.dispatchEvent(new Event('auth:logout'));
      }
    }
    return Promise.reject(err);
  },
);

// ─── Helpers d'erreur ──────────────────────────────────────────────────────

/** Forme normalisée de la réponse d'erreur du backend (cf. plugins/error-handler.ts). */
export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    details?: unknown;
  };
}

/** Extrait un message utilisateur lisible depuis une erreur Axios. */
export function extractErrorMessage(err: unknown, fallback = 'Une erreur est survenue'): string {
  if (axios.isAxiosError(err)) {
    const axiosErr = err as AxiosError<ApiErrorBody>;
    const body = axiosErr.response?.data;
    if (body?.error?.message) return body.error.message;
    if (axiosErr.message) return axiosErr.message;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}
